/**
 * web-studio-sites D13 — the page mover. Every move a piece, page or navigation
 * helper makes goes through here, never through React Router directly.
 *
 * On the standard website a move is React Router's own. On a studio's custom
 * site the page's data block (site.ts) names the addresses the site owns, so the
 * mover never guesses:
 * - on one of the site's own pages (the kit's placeholders), every move is a
 *   page load to the address it resolves to;
 * - on Telify's pages a move stays in the page unless it targets an address
 *   the site owns. The standard home goes to `links.home`, the services and
 *   events lists to `links.book`, the standard About to the site's `/about` or
 *   `links.home` when it has none. Those load her page.
 * A replacement stays a replacement, so Back never returns to it.
 */
import { useCallback, useEffect, useLayoutEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { readSite, type SiteData } from './site';

export interface MoveOptions {
  readonly replace?: boolean;
}

/** Moves to an address, or by a number of history entries (-1 is Back), as React Router's navigate does. */
export type Move = (to: string | number, options?: MoveOptions) => void;

/** Where a move lands: inside Telify's page, or a page load. */
export type Landing = { readonly load: false; readonly to: string } | { readonly load: true; readonly href: string };

/** The browser's own page loads and Back. Tests watch these. */
export const pageLoad = {
  assign(href: string): void {
    window.location.assign(href);
  },
  replace(href: string): void {
    window.location.replace(href);
  },
  back(): void {
    window.history.back();
  },
};

const BASE = 'https://telify.invalid';

/** A path as the server matches it: one trailing slash dropped (route-class.ts `normalizePath`), so /book/ is /book. */
function sitePath(pathname: string): string {
  return pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
}

/** The site's own address for one of the standard website's pages, or null for any other address. */
function siteAddressFor(rawPathname: string, hash: string, site: SiteData): string | null {
  const pathname = sitePath(rawPathname);
  const about = site.pages.includes('/about') ? '/about' : site.links.home.path;
  if (pathname === '/') {
    if (hash === '#about') return about;
    if (hash === '#services' || hash === '#events') return site.links.book.path;
    return site.links.home.path;
  }
  if (pathname === '/about') return about;
  if (pathname === '/services' || pathname === '/events') return site.links.book.path;
  return null;
}

/**
 * Where `to` lands. `onSitePage`: the page on show is one of the site's own,
 * where Telify draws only placeholders, so nothing moves inside it.
 */
export function resolveMove(to: string, site: SiteData | null, onSitePage: boolean): Landing {
  if (!site) return { load: false, to };
  const url = new URL(to, BASE);
  const standard = siteAddressFor(url.pathname, url.hash, site);
  if (standard !== null) return { load: true, href: standard };
  const address = `${url.pathname}${url.search}${url.hash}`;
  if (onSitePage || site.pages.includes(sitePath(url.pathname))) return { load: true, href: address };
  return { load: false, to: address };
}

function onSitePage(site: SiteData | null, pathname: string): boolean {
  return site !== null && site.pages.includes(sitePath(pathname));
}

/** The address a move to `to` lands on, for a link's `href`: a new tab opens where a click would go. */
export function useMoveHref(to: string): string {
  const { pathname } = useLocation();
  const site = readSite();
  const landing = resolveMove(to, site, onSitePage(site, pathname));
  return landing.load ? landing.href : landing.to;
}

/** The mark on the tab's first entry of Telify's page: whether the tab held one of her pages before it. */
const EARLIER_PAGE = 'telifyEarlierPage';

/** Read as the page loads: the tab already held a page, and it was one of hers. */
function cameFromThisAddressInThisTab(): boolean {
  if (window.history.length <= 1) return false;
  try {
    return new URL(document.referrer).origin === window.location.origin;
  } catch {
    return false;
  }
}

/**
 * On a custom site, marks the tab's first entry of Telify's page once, while it is the page that just loaded. Going
 * forward lengthens the history later, so Back reads this mark, never the history's length then. The mark sits
 * beside React Router's own fields in the entry's state.
 */
function useMarkFirstEntry(key: string, sitePage: boolean): void {
  useLayoutEffect(() => {
    if (key !== 'default' || sitePage || !readSite()) return;
    const state = window.history.state as Record<string, unknown> | null;
    if (state && EARLIER_PAGE in state) return;
    window.history.replaceState({ ...state, [EARLIER_PAGE]: cameFromThisAddressInThisTab() }, '');
  }, [key, sitePage]);
}

/** The mover for this page. */
export function usePageMover(): Move {
  const navigate = useNavigate();
  const { pathname, key } = useLocation();
  useMarkFirstEntry(key, onSitePage(readSite(), pathname));
  return useCallback<Move>(
    (to, options) => {
      const site = readSite();
      const sitePage = onSitePage(site, pathname);
      if (typeof to === 'number') {
        if (sitePage) window.history.go(to);
        else navigate(to);
        return;
      }
      const landing = resolveMove(to, site, sitePage);
      if (!landing.load) navigate(landing.to, options);
      else if (options?.replace) pageLoad.replace(landing.href);
      else pageLoad.assign(landing.href);
    },
    [navigate, pathname],
  );
}

/** A redirect through the mover, in place of React Router's `Navigate`. Always a replacement. */
export function MoveTo({ to }: { readonly to: string }): null {
  const move = usePageMover();
  useEffect(() => move(to, { replace: true }), [move, to]);
  return null;
}

/** Whether the entry on show is the tab's first of Telify's page and was marked as having one of her pages before it. */
function hasEarlierPageOfHers(): boolean {
  return (window.history.state as Record<string, unknown> | null)?.[EARLIER_PAGE] === true;
}

/**
 * Back: where the visitor came from on this site. When Telify's page is the
 * first one they opened, the studio's home on the standard website; on a
 * custom site, the browser's Back when this tab held one of her pages before
 * it, and her booking page when they came from a link or opened a new tab,
 * however far forward they have been since (a page opened in a new tab carries
 * her address as its referrer too, but has nothing to go back to).
 */
export function useSiteBack(): () => void {
  const move = usePageMover();
  const { key } = useLocation();
  return useCallback(() => {
    if (key !== 'default') return move(-1);
    const site = readSite();
    if (!site) return move('/');
    if (hasEarlierPageOfHers()) pageLoad.back();
    else move(site.links.book.path);
  }, [key, move]);
}
